import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Snowflake, ArrowUpRight } from "lucide-react";

function samePath(a, b) {
  const norm = (p) => (p === "/Home" ? "/" : p || "/");
  return norm(a) === norm(b);
}

function useHashScroll() {
  const navigate = useNavigate();
  const loc = useLocation();
  return (to, e) => {
    if (!to || !to.includes("#")) return;
    const [path, hash] = to.split("#");
    const samePage = path === "" || samePath(path, loc.pathname);
    if (samePage && hash) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else navigate(to);
    }
  };
}

/* ------------------------------------------------------------------ */
/* Eyebrow label                                                       */
/* ------------------------------------------------------------------ */
export function Eyebrow({ icon: Icon, children, className = "", color }) {
  return (
    <span className={`eyebrow ${className}`} style={color ? { color } : undefined} data-testid="eyebrow-label">
      {Icon ? <Icon size={14} strokeWidth={2.5} /> : null}
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Staggered three-line heading                                        */
/* lines: [{ text, emphasis?: bool, accent?: hex }]                    */
/* ------------------------------------------------------------------ */
export function StaggeredHeading({ lines, animate = false, size = "big" }) {
  const sizes = {
    big: "text-4xl sm:text-6xl lg:text-7xl",
    hero: "text-5xl sm:text-7xl lg:text-8xl xl:text-[7rem]",
    md: "text-3xl sm:text-5xl lg:text-6xl",
  };
  return (
    <h1 className={`font-display uppercase tracking-tight leading-[0.9] ${sizes[size]}`}>
      {lines.filter((l) => l && l.text).map((l, i) => {
        const cls = l.emphasis ? "font-black italic" : "font-light";
        const style = l.emphasis ? { color: l.accent || "#2CACD4" } : undefined;
        const content = (
          <span className={`block ${cls}`} style={style}>
            {l.text}
          </span>
        );
        if (!animate) return <span key={i}>{content}</span>;
        return (
          <motion.span
            key={i}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.15, duration: 0.6, ease: "easeOut" }}
            className="block"
          >
            {content}
          </motion.span>
        );
      })}
    </h1>
  );
}

/* ------------------------------------------------------------------ */
/* Pill button (Link / anchor / button)                                */
/* ------------------------------------------------------------------ */
export function PillButton({ children, to, href, onClick, variant = "primary", icon: Icon, className = "", type, testid }) {
  const hashScroll = useHashScroll();
  const base = "inline-flex items-center gap-2 font-bold transition-all duration-300 cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2CACD4] focus-visible:ring-offset-2";
  const variants = {
    primary: "rounded-full bg-foreground text-background hover:bg-[#2CACD4] hover:text-slate-950 px-7 py-3.5 shadow-md hover:shadow-xl",
    ghost: "rounded-full underline decoration-[#2CACD4] decoration-2 underline-offset-4 hover:text-[#2CACD4] font-semibold px-3 py-2",
    dark: "rounded-full bg-white text-slate-950 hover:bg-[#CAE295] px-7 py-3.5 shadow-lg hover:shadow-xl",
    pink: "rounded-full bg-[#DB1A5C] text-white hover:bg-[#0F172A] px-7 py-3.5 shadow-md hover:shadow-xl",
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {Icon ? <Icon size={18} strokeWidth={2.5} /> : null}
    </>
  );
  if (to) return <Link to={to} onClick={(e) => hashScroll(to, e)} className={cls} data-testid={testid}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testid}>{inner}</a>;
  return <button type={type || "button"} onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
}

/* ------------------------------------------------------------------ */
/* Arrow-clipped image                                                 */
/* ------------------------------------------------------------------ */
export function ArrowImage({ src, alt, className = "", overlay }) {
  return (
    <figure className={`relative overflow-hidden ${className}`}>
      <img src={src} alt={alt} className="arrow-clip w-full h-full object-cover" loading="lazy" />
      {overlay}
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/* Scroll reveal                                                       */
/* ------------------------------------------------------------------ */
export function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/* Tilted card with rotated accent backdrop panel                      */
/* tilt/mt are literal tailwind classes passed from the page.          */
/* ------------------------------------------------------------------ */
export function TiltCard({ children, accent = "#2CACD4", tilt = "", mt = "", className = "", testid }) {
  return (
    <div className={`relative group ${mt}`} data-testid={testid}>
      <div
        className="absolute inset-x-5 top-8 h-[calc(100%-2rem)] rounded-3xl -z-10 hidden lg:block"
        style={{ background: `${accent}22`, transform: "rotate(-5deg)" }}
        aria-hidden="true"
      />
      <div
        className={`relative bg-card rounded-3xl border border-border shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${tilt} ${className}`}
      >
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tilted marquee ribbon                                               */
/* ------------------------------------------------------------------ */
export function MarqueeRibbon({ words }) {
  const items = (words || "Move · Together · Canada").split("·").map((w) => w.trim()).filter(Boolean);
  const strip = (
    <div className="flex shrink-0 items-center">
      {items.map((w, i) => (
        <span key={i} className="flex items-center gap-4 px-4 font-display font-black italic uppercase text-lg sm:text-2xl text-slate-950 whitespace-nowrap">
          {w}
          <Snowflake size={22} className="text-slate-950/70" strokeWidth={2.5} />
        </span>
      ))}
    </div>
  );
  return (
    <div className="relative my-10 -mx-4 sm:-mx-8" data-testid="marquee-ribbon">
      <div className="rotate-[-1.5deg] bg-[#2CACD4] py-3.5 overflow-hidden shadow-lg border-y border-black/10">
        <div className="flex w-max animate-marquee">
          {strip}
          {strip}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Rotating conic "Join In" badge                                      */
/* ------------------------------------------------------------------ */
export function JoinInBadge({ to = "/Home#participate", size = 240 }) {
  const center = Math.round(size * 0.42);
  const hashScroll = useHashScroll();
  return (
    <Link
      to={to}
      onClick={(e) => hashScroll(to, e)}
      data-testid="join-in-badge"
      className="relative inline-flex items-center justify-center cursor-pointer group"
      style={{ width: `min(${size}px, 82vw)`, height: `min(${size}px, 82vw)` }}
      aria-label="Join in — Show us your moves"
    >
      <div
        className="absolute inset-0 rounded-full animate-[spin_22s_linear_infinite] shadow-xl"
        style={{ background: "conic-gradient(from 0deg, #2CACD4, #DB1A5C, #2CB6C2, #CAE295, #2CACD4)" }}
        aria-hidden="true"
      />
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[spin_22s_linear_infinite]" aria-hidden="true">
        <defs>
          <path id="join-circle" d="M 50,50 m -40,0 a 40,40 0 1,1 80,0 a 40,40 0 1,1 -80,0" />
        </defs>
        <text fontSize="7" fontWeight="800" letterSpacing="1.8" className="font-display uppercase" fill="#ffffff">
          <textPath href="#join-circle" startOffset="0">JOIN IN · SHOW US YOUR MOVES · JOIN IN · SHOW US YOUR MOVES · </textPath>
        </text>
      </svg>
      <span
        className="relative z-10 flex flex-col items-center justify-center rounded-full bg-white shadow-lg group-hover:scale-105 transition-transform"
        style={{ width: center, height: center }}
      >
        <ArrowUpRight size={Math.round(center * 0.28)} className="text-[#2CACD4]" strokeWidth={2.5} />
        <span className="font-display font-black uppercase text-slate-950 leading-none tracking-tight" style={{ fontSize: Math.max(12, Math.round(center * 0.13)) }}>Join In</span>
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/* Auto-rotating quote / testimonial carousel (shows every quote)      */
/* ------------------------------------------------------------------ */
export function QuoteRotator({ quotes = [], align = "center" }) {
  const list = (quotes || []).filter((q) => q && q.text);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (list.length <= 1) return undefined;
    const id = setInterval(() => setI((v) => (v + 1) % list.length), 6500);
    return () => clearInterval(id);
  }, [list.length]);
  if (!list.length) return null;
  const idx = Math.min(i, list.length - 1);
  const q = list[idx];
  return (
    <div className={align === "center" ? "text-center" : ""} data-testid="quote-rotator">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        >
          <blockquote className="font-display font-light italic text-2xl sm:text-3xl leading-snug">"{q.text}"</blockquote>
          {q.attribution && <figcaption className="mt-4 text-sm font-bold uppercase tracking-wider text-[#DB1A5C]">— {q.attribution}</figcaption>}
        </motion.div>
      </AnimatePresence>
      {list.length > 1 && (
        <div className={`mt-5 flex gap-2 ${align === "center" ? "justify-center" : ""}`}>
          {list.map((_, d) => (
            <button
              key={d}
              onClick={() => setI(d)}
              aria-label={`Show quote ${d + 1}`}
              className={`h-2 rounded-full transition-all cursor-pointer ${d === idx ? "w-6 bg-[#DB1A5C]" : "w-2 bg-muted-foreground/40 hover:bg-muted-foreground/70"}`}
              data-testid={`quote-dot-${d}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
